import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { useMemo } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import NotAvailable from '@/components/ui/NotAvailable'
import Pagination from '@/components/ui/Pagination'
import Skeleton from '@/components/ui/Skeleton'
import StockAvatar from '@/components/ui/StockAvatar'
import TabBar, { type TabBarOption } from '@/components/ui/TabBar'
import TermTip from '@/components/ui/TermTip'
import TitleRow from '@/components/ui/TitleRow'
import { LOCAL_AVAILABLE, PIPELINE_AVAILABLE } from '@/features/ipo/api/ipoAvailability'
import { useIssuesList } from '@/features/ipo/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { shortBsLabel } from '@/lib/bsDate'
import { formatMoney, formatQuantity } from '@/lib/format'
import { MARKET_GLOSSARY } from '@/lib/market-glossary'
import { sentenceCase } from '@/lib/text'
import { resolveViewState } from '@/lib/view-state'
import { issueListStatusSchema, type IssueRow, type IssueType } from '@/schemas/ipo'
import {
  DEFAULT_TYPE,
  loadPublicIssues,
  publicIssuesSearchSchema,
} from '@/screens/ipo/PublicIssuesScreen.loader'
import { pageSizeSchema } from '@/schemas/table'

const HEADER = <TitleRow title="Public Issues" />

/** The default page size's rows (`tableQuerySchema` opens on ten), shorter on a last page. */
const SKELETON_ROWS = 10

/**
 * The loaded page's one block, in the order it draws it: the status tabs (36), the table (head
 * band and ten 60px two-line rows) and `Pagination`'s own row 24 under it.
 */
function PublicIssuesPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading public issues…"
      header={HEADER}
      pageTitle="Public Issues"
    >
      <div className="flex flex-col gap-4">
        <div className="flex h-9 items-center gap-5">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-14" />
        </div>
        <div>
          <DataTableSkeleton rows={SKELETON_ROWS} columns={5} rowHeight="xl" />
          <div className="mt-6 flex h-11 items-center justify-between gap-3 lg:h-8">
            <Skeleton className="h-4 w-36 rounded-xs" />
            <Skeleton className="h-4 w-44 rounded-xs" />
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}

function PublicIssuesError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load public issues"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/market/issues')({
  validateSearch: publicIssuesSearchSchema,
  loader: loadPublicIssues,
  pendingComponent: PublicIssuesPending,
  errorComponent: PublicIssuesError,
  component: PublicIssuesScreen,
})

/**
 * The status tabs, bare words: the issue type is the section strip's, so the page itself only
 * switches between the windows. Pipeline is the mock's fourth: mm-be does not serve it yet
 * (`PIPELINE_AVAILABLE`), so it is drawn and says so instead of asking.
 */
const STATUSES: readonly TabBarOption[] = [
  { value: 'open', label: 'Current' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'closed', label: 'Closed' },
  { value: 'pipeline', label: 'Pipeline' },
]

/** The wire says "At Par" / "Premium"; the design prints "At par" / "At premium". */
const sentenceBadge = (badge: string): string => {
  const lower = badge.trim().toLowerCase()
  const full = lower === 'premium' ? 'at premium' : lower
  return full.charAt(0).toUpperCase() + full.slice(1)
}

/**
 * Two letters, as the design draws: the symbol's first two (SMTH → SM); an unlisted issue with no
 * symbol falls back to its company name's.
 */
const initialsOf = (row: IssueRow): string =>
  (row.symbol ?? row.company)
    .replace(/[^A-Za-z]/g, '')
    .slice(0, 2)
    .toUpperCase()

/**
 * "NABIL, promoter shares": an auction's symbol and which of its shares go under the hammer.
 * Every other type's subtitle is the symbol alone — mm-be carries no issue manager or rating
 * agency per issue, so the mock's "SMTH, Global IME Capital, ICRA" cannot be drawn
 * (`asks/pi-public-issues.md` A4). Plain text, not a stock link: the whole row is the link.
 */
const subtitleOf = (row: IssueRow): string | null => {
  if (row.symbol === null) return null
  return row.shareType === null
    ? row.symbol
    : `${row.symbol}, ${row.shareType.toLowerCase()} shares`
}

/**
 * A head that explains itself: the mock's dotted term with its band card (`pi-tip`, owner A3).
 * The card's title follows the head's own label where ours differs from the mock's generic one.
 */
const headTerm = (term: keyof typeof MARKET_GLOSSARY, label: string) => () => (
  <TermTip term={term} variant="band">
    {label}
  </TermTip>
)

const priceHead = (label: string) => () =>
  label === 'Issue price' ? (
    <TermTip term="issuePriceAtPar" variant="band">
      {label}
    </TermTip>
  ) : (
    <TermTip title={label} body={MARKET_GLOSSARY.issuePrice.body} variant="band">
      {label}
    </TermTip>
  )

/**
 * Company / Ratio / Units / Price / Opens / Closes / Prospectus, at the mock's column widths
 * (`pi-run` measure: Units 68, Price 80, Opens 68, Closes 68, Doc 76, Ratio 56 — each middle
 * column carries its 12 gap) with the company taking the rest, so the figures hold their place
 * from 1440 to 1920 instead of drifting apart. None of it invented:
 *
 * - `ratio` only for Right — every other type's rows carry a null `ratio`.
 * - `priceHeader` is mm-be's own `price_label` per row ("Issue Price" / "Right Price" / …) set
 *   in sentence case, where the mock prints a generic "Price".
 * - `prospectusHeader` reads "Notice" only for Auction; the cell is a document link either way.
 *   The column is drawn only when a row on screen carries a link (`showDocument`): mm-be keeps no
 *   document for rights and auctions, and a column of dashes is not data.
 * - Auction's "Min bid price / Min bid qty" are NOT reproduced: nothing on `IssueRow` says whether
 *   `units` is a total lot or a minimum bid, and mislabelling a real number is worse than the
 *   generic "Units" (ask A10).
 * - Headers are plain terms: mm-be's list has no sort parameter, so no column pretends to sort.
 * - `hideBelow` drops the opening date first (`2xl`), then the closing date (`xl`), then the
 *   document link (`lg`), then the units (`sm`).
 */
const buildColumns = (
  priceHeader: string,
  showRatio: boolean,
  showDocument: boolean,
  prospectusHeader: string,
): readonly DataTableColumn<IssueRow>[] => {
  const ratioColumn: DataTableColumn<IssueRow> = {
    id: 'ratio',
    accessorFn: (row) => row.ratio,
    header: headTerm('ratio', 'Ratio'),
    enableSorting: false,
    cell: ({ row }) => row.original.ratio ?? '—',
    meta: { numeric: true, secondary: true, width: '4.25rem' },
  }

  const documentColumn: DataTableColumn<IssueRow> = {
    id: 'prospectus',
    accessorFn: (row) => row.prospectus,
    header: headTerm(prospectusHeader === 'Notice' ? 'notice' : 'prospectus', prospectusHeader),
    enableSorting: false,
    cell: ({ row }) =>
      row.original.prospectus === null ? (
        '—'
      ) : (
        <a
          href={row.original.prospectus}
          target="_blank"
          rel="noopener noreferrer"
          // Every row's link reads "PDF" — sensible beside the company name
          // it sits next to, but indistinguishable from every other row's
          // "PDF" to a screen reader walking the page's links out of table
          // context. `aria-label` carries the row it belongs to.
          aria-label={`${prospectusHeader} for ${row.original.company}, opens in a new tab`}
          // The mock's `pi-doc`: gold, 400; hover lifts it to `--accent-hover` and underlines
          // (offset 3) while the glyph nudges 1px up and right.
          className="group/pdf inline-flex items-center gap-1 text-accent underline-offset-3 transition-colors duration-fast ease-out hover:text-accent-hover hover:underline"
        >
          PDF{' '}
          <ExternalLink
            className="size-3 transition-transform duration-fast ease-out group-hover/pdf:motion-safe:translate-x-px group-hover/pdf:motion-safe:-translate-y-px"
            aria-hidden="true"
          />
        </a>
      ),
    meta: { align: 'right', hideBelow: 'lg', width: '4.75rem' },
  }

  return [
    {
      id: 'company',
      accessorFn: (row) => row.company,
      header: 'Company',
      enableSorting: false,
      cell: ({ row }) => {
        const subtitle = subtitleOf(row.original)
        return (
          <div className="flex min-w-0 items-center gap-3">
            {/* The company's mark where the CDN has one for its symbol, its two letters
                otherwise (an unlisted issue's symbol is not on the board yet). Decorative:
                the company's name is printed beside it. */}
            <span aria-hidden="true" className="flex shrink-0">
              <StockAvatar
                symbol={row.original.symbol ?? ''}
                monogram={initialsOf(row.original)}
                size={36}
              />
            </span>
            <div className="flex min-w-0 flex-col">
              {/* `data-row-name`: the table underlines it while the row is hovered or focused. */}
              <span
                data-row-name=""
                title={row.original.company}
                className="truncate text-h-s text-foreground"
              >
                {row.original.company}
              </span>
              {subtitle === null ? null : (
                <span className="truncate text-body-xs text-muted-foreground-subtle">
                  {subtitle}
                </span>
              )}
            </div>
          </div>
        )
      },
      // Issuer names run long (full Nepali institution names): the company takes every pixel the
      // figures leave and truncates.
      meta: { fill: { min: 168 } },
    },
    ...(showRatio ? [ratioColumn] : []),
    {
      id: 'units',
      accessorFn: (row) => row.units,
      header: headTerm('issueUnits', 'Units'),
      enableSorting: false,
      cell: ({ row }) => (row.original.units === null ? '—' : formatQuantity(row.original.units)),
      meta: { numeric: true, secondary: true, hideBelow: 'sm', width: '5rem' },
    },
    {
      id: 'price',
      accessorFn: (row) => row.price,
      header: priceHead(priceHeader),
      enableSorting: false,
      cell: ({ row }) =>
        row.original.price === null ? (
          '—'
        ) : (
          <span className="flex flex-col items-end">
            <span className="text-foreground">Rs {formatMoney(row.original.price)}</span>
            {row.original.pricingBadge === null ? null : (
              <span className="text-body-xs text-muted-foreground-subtle">
                {sentenceBadge(row.original.pricingBadge)}
              </span>
            )}
          </span>
        ),
      meta: { numeric: true, width: '5.75rem' },
    },
    {
      id: 'opens',
      accessorFn: (row) => row.opens,
      header: headTerm('issueOpens', 'Opens'),
      enableSorting: false,
      cell: ({ row }) => shortBsLabel(row.original.opens),
      meta: { align: 'right', secondary: true, hideBelow: '2xl', width: '5rem' },
    },
    {
      id: 'closes',
      accessorFn: (row) => row.closes,
      header: headTerm('issueCloses', 'Closes'),
      enableSorting: false,
      cell: ({ row }) => shortBsLabel(row.original.closes),
      meta: { align: 'right', secondary: true, hideBelow: 'xl', width: '5rem' },
    },
    ...(showDocument ? [documentColumn] : []),
  ]
}

function PublicIssuesScreen() {
  const { type, status, page, pageSize } = Route.useSearch()
  const navigate = Route.useNavigate()
  const activeType = type ?? DEFAULT_TYPE

  /**
   * The mock's IPO-Local type and Pipeline window are not served by mm-be (asks A1, A2): picking
   * either draws the tab and "Not available yet", and no request is made. The query below is
   * switched off for them, so the placeholder type and status it carries are never sent.
   */
  const unavailable =
    (activeType === 'local' && !LOCAL_AVAILABLE) || (status === 'pipeline' && !PIPELINE_AVAILABLE)
  const wireType: IssueType = activeType === 'local' ? DEFAULT_TYPE : activeType
  const wireStatus = status === 'pipeline' ? 'open' : status
  const query = useIssuesList({ type: wireType, status: wireStatus, page, pageSize }, !unavailable)
  const online = useOnline()

  const rows = query.data?.rows ?? []
  const total = query.data?.total ?? 0
  // Data on screen wins over a failed background refetch, offline explains a stalled first load
  // instead of leaving it "loading" forever, and only then does an actual error read as failed.
  // See lib/view-state.ts.
  const view = resolveViewState(query, { isEmpty: (data) => data.rows.length === 0, online })

  const priceHeader = sentenceCase(rows[0]?.priceLabel ?? 'Price')
  const showRatio = activeType === 'right'
  const showDocument = rows.some((row) => row.prospectus !== null)
  const prospectusHeader = activeType === 'auction' ? 'Notice' : 'Prospectus'
  // Rebuilt only when a heading changes, so a refetch never remounts the cells (see DataTable's
  // `columns`).
  const columns = useMemo(
    () => buildColumns(priceHeader, showRatio, showDocument, prospectusHeader),
    [priceHeader, showRatio, showDocument, prospectusHeader],
  )

  return (
    <ScreenLayout header={HEADER} pageTitle="Public Issues">
      {/* The status tabs and the table are one block, 16 apart (the pages revamp's one gap from
          a tab row to its first block). The tabs sit on the page, not in a card: the table is its
          own panel. Table and pager travel as ONE block of the stack: `Pagination` draws its own
          24px (see its PLACEMENT note) and a parent `gap-*` would add to it rather than absorb
          it. */}
      <div className="flex flex-col gap-4">
        <TabBar
          options={STATUSES}
          value={status}
          onChange={(value) => {
            void navigate({
              search: (prev) => ({ ...prev, status: issueListStatusSchema.parse(value), page: 1 }),
            })
          }}
          ariaLabel="Issue status"
        />

        {unavailable ? (
          <NotAvailable thing={status === 'pipeline' ? 'Pipeline issues' : 'IPO-Local issues'} />
        ) : (
          <div>
            <DataTable
              columns={columns}
              rows={rows}
              getRowId={(row) => row.key}
              caption="Public issues"
              isLoading={view === 'loading'}
              emptyState={
                view === 'failed' || view === 'offline' ? (
                  <EmptyState
                    kind={view}
                    title="Couldn't load public issues"
                    onRetry={() => void query.refetch()}
                    retrying={query.isFetching}
                    savedAt={query.dataUpdatedAt}
                  />
                ) : (
                  <EmptyState
                    kind="no-results"
                    title="No issues in this view"
                    description="Choose another type or status."
                  />
                )
              }
              // The mock's 60px row: a 36 mark with 12 above and below.
              rowHeight="xl"
              // The whole row opens the issue (mock `pi-row`): wash and name underline on hover.
              rowLinkOptions={(row) => ({ to: '/ipo/$key', params: { key: row.key } })}
              headWeight="regular"
            />

            {total > 0 ? (
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={(next) => {
                  void navigate({ search: (prev) => ({ ...prev, page: next }) })
                }}
                onPageSizeChange={(next) => {
                  void navigate({
                    search: (prev) => ({ ...prev, pageSize: pageSizeSchema.parse(next), page: 1 }),
                  })
                }}
                unit="issues"
                size="lg"
              />
            ) : null}
          </div>
        )}
      </div>
    </ScreenLayout>
  )
}
